/**
 * Doors and boss seals — OWNER: spatial builder.
 * Door state lives in `World.doors` (serializable). The nav data for a given set of closed
 * doors is a derived cache (see `withBlockers`), shared by every sim on the same DungeonDef.
 */
import type { DoorDef, DoorState, DungeonDef, EncounterId, Polygon, RoomId, SimContext, Unit, UnitId, Vec2, World } from '../contracts';
import { DOOR_BLOCK_DEPTH, SEAL_GRACE_MS } from '../contracts';
import { pointInPolygon } from './geometry';
import { clampToWalkable, circleWalkable, withBlockers, type NavData } from './nav';

/** Stragglers pulled in by a seal land this far inside the arena room (past its edge). */
const PULL_IN_DIST = 3;
/** Lateral spacing between pulled-in stragglers. */
const PULL_IN_SPREAD = 2;
/** Give up looking for the arena past the door after this many yards. */
const MAX_PULL_SEARCH = 40;

/** Blocking footprint of a door: `width` across, `depth` along `facing`, centred on `position`. */
export function doorPolygon(d: DoorDef): Polygon {
  const hd = (d.depth ?? DOOR_BLOCK_DEPTH) / 2;
  const hw = d.width / 2;
  const fx = Math.cos(d.facing);
  const fy = Math.sin(d.facing);
  // Across = facing rotated +90°.
  const ax = -fy;
  const ay = fx;
  const p = d.position;
  return {
    points: [
      { x: p.x - fx * hd - ax * hw, y: p.y - fy * hd - ay * hw },
      { x: p.x + fx * hd - ax * hw, y: p.y + fy * hd - ay * hw },
      { x: p.x + fx * hd + ax * hw, y: p.y + fy * hd + ay * hw },
      { x: p.x - fx * hd + ax * hw, y: p.y - fy * hd + ay * hw },
    ],
  };
}

/** Initial door states for a dungeon (empty record when it has none). */
export function initialDoorStates(dungeon: DungeonDef | undefined): Record<string, DoorState> {
  const out: Record<string, DoorState> = {};
  for (const d of dungeon?.doors ?? []) out[d.id] = { closed: d.initially === 'closed', changedAt: 0 };
  return out;
}

/** Nav variants per base nav, keyed by the bitmask of closed doors (door order in the DungeonDef). */
const variants = new WeakMap<NavData, Map<number, NavData>>();

export function closedMask(dungeon: DungeonDef, world: World): number {
  const doors = dungeon.doors;
  if (!doors || !world.doors) return 0;
  let mask = 0;
  for (let i = 0; i < doors.length && i < 31; i++) if (world.doors[doors[i]!.id]?.closed) mask |= 1 << i;
  return mask;
}

/** Nav data with the currently closed doors as blockers. */
export function navWithDoors(base: NavData, dungeon: DungeonDef | undefined, world: World): NavData {
  if (!dungeon?.doors?.length || base.open) return base;
  const mask = closedMask(dungeon, world);
  if (mask === 0) return base;
  let byMask = variants.get(base);
  if (!byMask) {
    byMask = new Map();
    variants.set(base, byMask);
  }
  let n = byMask.get(mask);
  if (!n) {
    const polys = dungeon.doors.filter((_, i) => (mask & (1 << i)) !== 0).map(doorPolygon);
    n = withBlockers(base, polys);
    byMask.set(mask, n);
  }
  return n;
}

/** Room of the boss pack for an encounter (the arena a seal protects). */
function arenaRoom(dungeon: DungeonDef, encounterId: EncounterId): RoomId | null {
  return dungeon.packs.find((p) => p.encounterId === encounterId)?.roomId ?? null;
}

function inRoom(dungeon: DungeonDef, roomId: RoomId, p: Vec2): boolean {
  const r = dungeon.rooms.find((x) => x.id === roomId);
  return !!r && pointInPolygon(p, r.shape);
}

/** Unit vector from the door toward the arena room. */
function intoArena(d: DoorDef, arena: RoomId): Vec2 {
  const s = d.between[1] === arena ? 1 : -1;
  return { x: Math.cos(d.facing) * s, y: Math.sin(d.facing) * s };
}

function setDoor(ctx: SimContext, d: DoorDef, closed: boolean, extra: { encounterId?: EncounterId; pulledIds?: UnitId[] }): void {
  const st = (ctx.world.doors ??= {});
  st[d.id] = { closed, changedAt: ctx.now };
  if (closed) {
    const e: { type: 'door_close'; doorId: string; encounterId?: EncounterId; pulledIds?: UnitId[] } = { type: 'door_close', doorId: d.id };
    if (extra.encounterId) e.encounterId = extra.encounterId;
    if (extra.pulledIds?.length) e.pulledIds = extra.pulledIds;
    ctx.emit(e);
  } else {
    const e: { type: 'door_open'; doorId: string; encounterId?: EncounterId } = { type: 'door_open', doorId: d.id };
    if (extra.encounterId) e.encounterId = extra.encounterId;
    ctx.emit(e);
  }
}

/**
 * Per tick (start of moveUnits): drive boss seals from the encounter state.
 * - Close once the sealed encounter is in progress and every living party member is in the
 *   arena room, or SEAL_GRACE_MS after it started (stragglers are pulled inside first).
 * - Open as soon as that encounter is no longer in progress (kill, wipe or reset).
 */
export function updateDoors(ctx: SimContext, dungeon: DungeonDef | undefined, baseNav: NavData): void {
  const doors = dungeon?.doors;
  if (!dungeon || !doors?.length) return;
  const w = ctx.world;
  w.doors ??= initialDoorStates(dungeon);
  const enc = w.encounter && w.encounter.result === null ? w.encounter : null;

  // Open seals whose encounter is over.
  for (const d of doors) {
    if (!d.sealsEncounter) continue;
    const st = w.doors[d.id];
    if (st?.closed && enc?.encounterId !== d.sealsEncounter) setDoor(ctx, d, false, { encounterId: d.sealsEncounter });
  }
  if (!enc) return;
  const seals = doors.filter((d) => d.sealsEncounter === enc.encounterId && !w.doors![d.id]?.closed);
  if (seals.length === 0) return;
  const arena = arenaRoom(dungeon, enc.encounterId);
  if (!arena) return;

  const living = w.partyIds.map((id) => w.units[id]).filter((u): u is Unit => !!u?.alive);
  const outside = living.filter((u) => !inRoom(dungeon, arena, u.pos));
  if (outside.length > 0 && ctx.now - enc.startedAt < SEAL_GRACE_MS) return;

  // Pull stragglers in through the seal nearest to each of them, then close every seal.
  const pulled: UnitId[] = [];
  const open = navWithDoors(baseNav, dungeon, w);
  outside.forEach((u, i) => {
    let best = seals[0]!;
    let bestD = Infinity;
    for (const d of seals) {
      const dd = Math.hypot(u.pos.x - d.position.x, u.pos.y - d.position.y);
      if (dd < bestD) {
        bestD = dd;
        best = d;
      }
    }
    const dir = intoArena(best, arena);
    // Walk from the door toward the arena until inside the room, then PULL_IN_DIST further.
    let s = (best.depth ?? DOOR_BLOCK_DEPTH) / 2;
    const at = (k: number): Vec2 => ({ x: best.position.x + dir.x * k, y: best.position.y + dir.y * k });
    while (s < MAX_PULL_SEARCH && !inRoom(dungeon, arena, at(s))) s += 0.5;
    const entry = at(s);
    if (inRoom(dungeon, arena, at(s + PULL_IN_DIST))) s += PULL_IN_DIST;
    const lat = (i - (outside.length - 1) / 2) * PULL_IN_SPREAD;
    const c = at(s);
    let to = { x: c.x - dir.y * lat, y: c.y + dir.x * lat };
    if (!circleWalkable(open, to, 0.5) || !inRoom(dungeon, arena, to)) to = inRoom(dungeon, arena, c) && circleWalkable(open, c, 0.5) ? c : clampToWalkable(open, entry);
    u.pos = to;
    u.move = null;
    pulled.push(u.id);
  });
  seals.forEach((d, i) => setDoor(ctx, d, true, { encounterId: enc.encounterId, pulledIds: i === 0 ? pulled : undefined }));
}
